import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, Plus, Trash2, Edit2, Loader2, KeyRound, CheckCircle2, XCircle, Power } from 'lucide-react';
import { toast } from 'sonner';
import {
  adminTeamService, ADMIN_PERMESSI_LABELS, AdminTeamMember, AdminTeamMemberCreate,
} from '../../services/adminTeamService';
import type { AdminPermission } from '../../types';

const ALL_PERMESSI = Object.keys(ADMIN_PERMESSI_LABELS) as AdminPermission[];

export default function AdminTeamPage() {
  const [members, setMembers] = useState<AdminTeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AdminTeamMember | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setMembers(await adminTeamService.list());
    } catch {
      toast.error('Errore nel caricamento del team');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleAttivo = async (m: AdminTeamMember) => {
    try {
      await adminTeamService.update(m.userId, { attivo: !m.attivo });
      toast.success(m.attivo ? 'Operatore disattivato' : 'Operatore riattivato');
      load();
    } catch {
      toast.error('Errore durante l\'aggiornamento');
    }
  };

  const remove = async (m: AdminTeamMember) => {
    if (!window.confirm(`Eliminare definitivamente l'account di ${m.nome} ${m.cognome} (${m.email})?`)) return;
    try {
      await adminTeamService.remove(m.userId);
      toast.success('Operatore eliminato');
      load();
    } catch {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  return (
    <div className="p-6" data-testid="admin-team-page">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-[hsl(214,58%,41%)]/10 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-[hsl(214,58%,41%)]" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Team Amministrativo</h1>
        </div>
        <button
          onClick={() => { setEditing(null); setDialogOpen(true); }}
          className="h-9 px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium flex items-center gap-1.5 hover:bg-[hsl(214,58%,46%)]"
          data-testid="team-create-btn"
        >
          <Plus className="w-4 h-4" /> Nuovo Operatore
        </button>
      </div>
      <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
        Crea account di backoffice con accesso limitato SOLO alle sezioni del pannello admin per cui assegni un potere. Diverso dagli "Operatori" lato cliente (sotto-account carrello nell'Area Personale dei clienti).
      </p>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : members.length === 0 ? (
        <div className="rounded-xl border bg-white p-10 text-center text-sm text-muted-foreground" data-testid="team-empty-state">
          Nessun operatore di backoffice creato. Clicca "Nuovo Operatore" per iniziare.
        </div>
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden">
          <table className="w-full text-sm" data-testid="team-table">
            <thead>
              <tr className="border-b bg-secondary/40 text-left text-xs text-muted-foreground uppercase tracking-wider">
                <th className="px-4 py-3 font-medium">Operatore</th>
                <th className="px-4 py-3 font-medium">Poteri assegnati</th>
                <th className="px-4 py-3 font-medium">Stato</th>
                <th className="px-4 py-3 font-medium text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {members.map(m => (
                <tr key={m.userId} className="border-b last:border-0" data-testid={`team-row-${m.userId}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{m.nome} {m.cognome}</div>
                    <div className="text-xs text-muted-foreground">{m.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    {m.permessiAdmin.length === 0 ? (
                      <span className="text-xs text-muted-foreground italic">Nessun potere assegnato</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {m.permessiAdmin.map(p => (
                          <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-[hsl(214,58%,41%)]/10 text-[hsl(214,58%,41%)] font-medium">
                            {ADMIN_PERMESSI_LABELS[p]?.label || p}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {m.attivo ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Attivo</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><XCircle className="w-3.5 h-3.5" /> Disattivato</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => { setEditing(m); setDialogOpen(true); }} title="Modifica" data-testid={`team-edit-${m.userId}`}
                        className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-secondary/60"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => toggleAttivo(m)} title={m.attivo ? 'Disattiva' : 'Riattiva'} data-testid={`team-toggle-${m.userId}`}
                        className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-secondary/60"><Power className="w-4 h-4" /></button>
                      <button onClick={() => remove(m)} title="Elimina" data-testid={`team-delete-${m.userId}`}
                        className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-red-50 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialogOpen && (
        <TeamMemberDialog
          member={editing}
          onClose={() => setDialogOpen(false)}
          onSaved={() => { setDialogOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function TeamMemberDialog({
  member, onClose, onSaved,
}: { member: AdminTeamMember | null; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!member;
  const [email, setEmail] = useState(member?.email ?? '');
  const [nome, setNome] = useState(member?.nome ?? '');
  const [cognome, setCognome] = useState(member?.cognome ?? '');
  const [password, setPassword] = useState('');
  const [permessi, setPermessi] = useState<AdminPermission[]>(member?.permessiAdmin ?? []);
  const [saving, setSaving] = useState(false);

  const togglePermesso = (p: AdminPermission) => {
    setPermessi(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  };

  const canSubmit = nome.trim() && cognome.trim() && (isEdit || (email.includes('@') && password.length >= 8));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      if (isEdit && member) {
        const payload: any = { nome, cognome, permessiAdmin: permessi };
        if (password) payload.password = password;
        await adminTeamService.update(member.userId, payload);
        toast.success('Operatore aggiornato');
      } else {
        const payload: AdminTeamMemberCreate = { email: email.trim(), password, nome, cognome, permessiAdmin: permessi };
        await adminTeamService.create(payload);
        toast.success('Operatore creato con successo');
      }
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6"
        data-testid="team-dialog"
      >
        <h3 className="text-lg font-semibold mb-4">{isEdit ? 'Modifica Operatore' : 'Nuovo Operatore di Backoffice'}</h3>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium block mb-1">Nome *</label>
              <input value={nome} onChange={e => setNome(e.target.value)} required
                className="w-full h-9 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)]/30" data-testid="team-input-nome" />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Cognome *</label>
              <input value={cognome} onChange={e => setCognome(e.target.value)} required
                className="w-full h-9 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)]/30" data-testid="team-input-cognome" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Email (usata per il login) *</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required disabled={isEdit}
              className="w-full h-9 px-3 rounded-lg border text-sm disabled:bg-secondary/40 disabled:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)]/30" data-testid="team-input-email" />
          </div>
          <div>
            <label className="text-xs font-medium flex items-center gap-1 mb-1"><KeyRound className="w-3 h-3" /> {isEdit ? 'Nuova password (opzionale)' : 'Password *'}</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={isEdit ? 'Lascia vuoto per non cambiarla' : 'Min. 8 caratteri'}
              className="w-full h-9 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)]/30" data-testid="team-input-password" />
          </div>
          <div>
            <label className="text-xs font-medium block mb-2">Poteri assegnati</label>
            <div className="space-y-2">
              {ALL_PERMESSI.map(p => (
                <label key={p} className="flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer hover:bg-secondary/40" data-testid={`team-permesso-${p}`}>
                  <input type="checkbox" checked={permessi.includes(p)} onChange={() => togglePermesso(p)} className="mt-0.5 accent-[hsl(214,58%,41%)]" data-testid={`team-permesso-check-${p}`} />
                  <div>
                    <div className="text-sm font-medium">{ADMIN_PERMESSI_LABELS[p].label}</div>
                    <div className="text-xs text-muted-foreground">{ADMIN_PERMESSI_LABELS[p].description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="h-9 px-4 rounded-lg border text-sm font-medium">Annulla</button>
          <button type="submit" disabled={!canSubmit || saving}
            className="h-9 px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2" data-testid="team-submit-btn">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? 'Salva Modifiche' : 'Crea Operatore'}
          </button>
        </div>
      </form>
    </div>
  );
}
